import { useEffect, useState } from "react";
import api, { withSite } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import DashboardTour from "@/components/DashboardTour";
import {
  Thermometer,
  AlertTriangle,
  ClipboardList,
  Printer,
  PackageOpen,
  Sparkles,
  CheckCircle2,
} from "lucide-react";

const StatTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="kpi">
    <div className="flex items-center justify-between">
      <span className="label-eyebrow">{label}</span>
      <Icon className={`w-5 h-5 ${accent || "text-eucalyptus-700"}`} />
    </div>
    <div className={`kpi-value ${accent || "text-charcoal"}`}>{value ?? "—"}</div>
  </div>
);

export default function Dashboard() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [printStats, setPrintStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeSite) return;
    setLoading(true);
    api
      .get("/dashboard", withSite(activeSite.id))
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    api
      .get("/print-logs/stats", withSite(activeSite.id))
      .then(({ data }) => setPrintStats(data))
      .catch(() => setPrintStats(null));
  }, [activeSite]);

  if (!activeSite) {
    return (
      <div className="surface p-10 text-center text-charcoal-soft">
        Select a site to view its dashboard.
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      <DashboardTour />
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">
            {activeSite.type.replace("_", " ")} · {activeSite.address}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">
            {activeSite.name}
          </h1>
          <p className="text-charcoal-soft mt-2">
            Hello {user?.name?.split(" ")[0] || "there"} — here's what's happening in your
            kitchen today.
          </p>
        </div>
        <div className="surface-alt px-4 py-3 flex items-center gap-3 font-mono text-xs">
          <CheckCircle2 className="w-4 h-4 text-eucalyptus-700" />
          <span>
            Compliance run:{" "}
            <strong className="text-eucalyptus-900">
              {new Date().toLocaleString("en-AU", { timeZone: "Australia/Sydney" })}
            </strong>
          </span>
        </div>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <StatTile
          testid="kpi-temp-logs"
          icon={Thermometer}
          label="Temp logs today"
          value={data?.temp_logs_today}
        />
        <StatTile
          testid="kpi-breaches"
          icon={AlertTriangle}
          label="Breaches / 7 days"
          value={data?.temp_breaches_week}
          accent={data?.temp_breaches_week ? "text-ochre-600" : "text-eucalyptus-700"}
        />
        <StatTile
          testid="kpi-open-ca"
          icon={ClipboardList}
          label="Open corrective actions"
          value={data?.open_corrective_actions}
          accent={data?.open_corrective_actions ? "text-ochre-600" : "text-eucalyptus-700"}
        />
        <StatTile
          testid="kpi-receival"
          icon={PackageOpen}
          label="Deliveries today"
          value={data?.receival_today}
        />
        <StatTile
          testid="kpi-cleaning"
          icon={Sparkles}
          label="Cleaning sign-offs"
          value={data?.cleaning_today}
        />
        <StatTile
          testid="kpi-checklists"
          icon={ClipboardList}
          label="Checklists completed"
          value={data?.checklists_today}
        />
        <StatTile
          testid="kpi-labels"
          icon={Printer}
          label="Labels printed today"
          value={printStats?.today}
        />
      </div>

      {/* Label printing pulse */}
      <section className="surface p-4 sm:p-6" data-testid="label-print-widget">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Printer className="w-5 h-5 text-eucalyptus-700" /> Label printing
          </h2>
          <span className="label-eyebrow">Tecor + browser</span>
        </div>
        <div className="grid md:grid-cols-2 gap-6 [&>*]:min-w-0">
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div data-testid="print-stat-today" className="surface-alt p-3 sm:p-4">
              <div className="label-eyebrow">Today</div>
              <div className="kpi-value text-eucalyptus-900">{printStats?.today ?? "—"}</div>
            </div>
            <div data-testid="print-stat-week" className="surface-alt p-3 sm:p-4">
              <div className="label-eyebrow">This week</div>
              <div className="kpi-value text-eucalyptus-900">{printStats?.week ?? "—"}</div>
            </div>
            <div data-testid="print-stat-total" className="surface-alt p-3 sm:p-4">
              <div className="label-eyebrow">All time</div>
              <div className="kpi-value text-charcoal">{printStats?.total ?? "—"}</div>
            </div>
          </div>
          <div>
            <div className="label-eyebrow mb-3">Top products</div>
            {!printStats?.top_products?.length ? (
              <div className="text-sm text-charcoal-soft py-4" data-testid="print-top-empty">
                No labels printed yet. Head to the Label Printer to get started.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="print-top-products">
                {printStats.top_products.map((p, i) => {
                  const max = printStats.top_products[0].count || 1;
                  return (
                    <li key={p.name} className="flex items-center gap-3 text-sm">
                      <span className="w-5 text-charcoal-soft font-mono text-xs">{i + 1}.</span>
                      <span className="flex-1 truncate font-medium">{p.name}</span>
                      <div className="w-24 h-2 rounded-full bg-eucalyptus-100 overflow-hidden">
                        <div className="h-full bg-ochre-500" style={{ width: `${(p.count / max) * 100}%` }} />
                      </div>
                      <span className="font-mono text-xs w-8 text-right">{p.count}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* Lists */}
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Recent temperature breaches</h2>
            <span className="label-eyebrow">Last 5</span>
          </div>
          {!data?.recent_breaches?.length ? (
            <div className="text-sm text-charcoal-soft py-8 text-center">
              No breaches recorded. Great work.
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--border)]">
              {data.recent_breaches.map((b) => (
                <li key={b.id} className="py-3 flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold">{b.equipment_name}</div>
                    <div className="text-xs text-charcoal-soft">
                      {new Date(b.created_at).toLocaleString("en-AU")} · {b.user_name}
                    </div>
                  </div>
                  <div className="font-mono text-ochre-600 font-bold">
                    {b.temperature_c}°C
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Open corrective actions</h2>
            <span className="label-eyebrow">Last 5</span>
          </div>
          {!data?.recent_corrective_actions?.length ? (
            <div className="text-sm text-charcoal-soft py-8 text-center">
              Nothing open — the kitchen's in the clear.
            </div>
          ) : (
            <ul className="space-y-3">
              {data.recent_corrective_actions.map((c) => (
                <li key={c.id} className="surface-alt p-3">
                  <div className="text-sm font-semibold">{c.issue}</div>
                  <div className="text-xs text-charcoal-soft mt-1">
                    {new Date(c.created_at).toLocaleString("en-AU")} · {c.user_name}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {loading && (
        <div className="text-xs font-mono text-charcoal-soft">Refreshing…</div>
      )}
    </div>
  );
}
